import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowLeft, faServer, faSync, faRocket, faExclamationTriangle,
  faCheckCircle, faClock, faNetworkWired
} from '@fortawesome/free-solid-svg-icons';
import { Breadcrumb } from '../components/Breadcrumb';
import { navigationState } from '../utils/navigationState';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001';

interface ClusterDetailsPageProps {
  clusterId: string;
  clusterName: string;
  authToken: string | null;
  onBack: () => void;
  onUpgrade: () => void;
}

export const ClusterDetailsPage: React.FC<ClusterDetailsPageProps> = ({
  clusterId,
  clusterName,
  authToken,
  onBack,
  onUpgrade
}) => {
  const [loading, setLoading] = useState(true);
  const [clusterData, setClusterData] = useState<any>(null);
  const [nodes, setNodes] = useState<any[]>([]);
  const [pods, setPods] = useState<any[]>([]);
  const [upgradeAnalysis, setUpgradeAnalysis] = useState<any>(null);
  const [activeTab, setActiveTab] = useState<'overview' | 'nodes' | 'pods'>('overview');
  const [loadingPods, setLoadingPods] = useState(false);

  useEffect(() => {
    fetchClusterDetails();
  }, [clusterId]);

  const fetchClusterDetails = async () => {
    setLoading(true);
    try {
      // Fetch nodes
      const nodesResponse = await fetch(
        `${API_BASE_URL}/api/accounts/eks/clusters/${clusterId}/resources?resourceType=nodes`,
        {
          headers: {
            'Authorization': `Bearer ${authToken}`,
            'Content-Type': 'application/json'
          }
        }
      );
      const nodesData = await nodesResponse.json();
      setNodes(nodesData.nodes || []);

      // Fetch upgrade analysis
      const upgradeResponse = await fetch(
        `${API_BASE_URL}/api/accounts/eks/clusters/${clusterId}/upgrade-analysis`,
        {
          headers: {
            'Authorization': `Bearer ${authToken}`,
            'Content-Type': 'application/json'
          }
        }
      );
      const upgradeData = await upgradeResponse.json();
      setUpgradeAnalysis(upgradeData);
      setClusterData(upgradeData.cluster);
    } catch (error) {
      console.error('Error fetching cluster details:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchPods = async () => {
    setLoadingPods(true);
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/accounts/eks/clusters/${clusterId}/resources?resourceType=pods`,
        {
          headers: {
            'Authorization': `Bearer ${authToken}`,
            'Content-Type': 'application/json'
          }
        }
      );
      const data = await response.json();
      setPods(data.pods || []);
    } catch (error) {
      console.error('Error fetching pods:', error);
    } finally {
      setLoadingPods(false);
    }
  };

  const handleTabChange = (tab: 'overview' | 'nodes' | 'pods') => {
    setActiveTab(tab);
    if (tab === 'pods' && pods.length === 0) {
      fetchPods();
    }
  };

  const breadcrumbs = navigationState.getBreadcrumbs();

  // Check for node group version mismatches
  const nodeGroupMismatches = nodes.filter(node => {
    const nodeVersion = node.version?.replace('v', '');
    const clusterVersion = clusterData?.currentVersion;
    return nodeVersion && clusterVersion && nodeVersion !== clusterVersion;
  });

  return (
    <div className="p-2 space-y-2">
      {/* Breadcrumb Navigation */}
      <Breadcrumb items={breadcrumbs} />

      {/* Header with Back Button */}
      <div className="flex items-center justify-between mb-2">
        <button
          onClick={onBack}
          className="flex items-center space-x-1 text-xs text-gray-600 hover:text-gray-900 transition-colors"
        >
          <FontAwesomeIcon icon={faArrowLeft} className="text-xs" />
          <span>Back</span>
        </button>
      </div>

      {/* Cluster Header */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-md p-2.5">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-base font-bold text-gray-900">{clusterName}</h1>
            <div className="flex items-center space-x-2 mt-0.5">
              <span className="text-xs text-gray-600">
                <FontAwesomeIcon icon={faServer} className="mr-1 text-xs" />
                v{clusterData?.currentVersion || '...'}
              </span>
              <span className={`px-1.5 py-0.5 rounded text-xs font-semibold ${
                clusterData?.status === 'ACTIVE' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
              }`}>
                {clusterData?.status || 'Unknown'}
              </span>
            </div>
          </div>
          <div className="flex space-x-1.5">
            <button
              onClick={fetchClusterDetails}
              disabled={loading}
              className="px-2.5 py-1 text-xs bg-white border border-gray-300 text-gray-700 rounded hover:bg-gray-50 transition-colors disabled:opacity-50"
            >
              <FontAwesomeIcon icon={faSync} className={`mr-1 text-xs ${loading ? 'animate-spin' : ''}`} />
              Sync
            </button>
            <button
              onClick={onUpgrade}
              className="px-2.5 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 transition-all"
              title={`Current: ${clusterData?.currentVersion}, Available: ${upgradeAnalysis?.upgradeOptions?.nextVersion || 'None'}`}
            >
              <FontAwesomeIcon icon={faRocket} className="mr-1 text-xs" />
              EKS Version Upgrade
            </button>
          </div>
        </div>
      </div>

      {/* Node Group Version Mismatch Alert */}
      {nodeGroupMismatches.length > 0 && (
        <div className="bg-red-50 border-l-2 border-red-500 p-2 rounded-sm">
          <div className="flex items-start">
            <FontAwesomeIcon icon={faExclamationTriangle} className="text-red-600 mt-0.5 mr-1.5 text-xs" />
            <div className="flex-1">
              <h3 className="text-xs font-semibold text-red-900 mb-0.5">Node Version Mismatch</h3>
              <p className="text-xs text-red-800 mb-1">
                Node groups must match control plane version
              </p>
              <ul className="space-y-0.5 mb-1.5">
                {nodeGroupMismatches.map((node, index) => (
                  <li key={index} className="text-xs text-red-800">
                    • {node.name}: {node.version} → v{clusterData?.currentVersion}
                  </li>
                ))}
              </ul>
              <button
                onClick={() => {
                  // Navigate to upgrade page to handle node group sync with progress monitoring
                  navigationState.goToUpgradeWizard(clusterId, clusterName, 'node-sync');
                }}
                className="px-2 py-0.5 text-xs bg-red-600 text-white rounded hover:bg-red-700 transition-colors"
              >
                Sync to {clusterData?.currentVersion}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Upgrade Status Alert */}
      {upgradeAnalysis?.warnings && upgradeAnalysis.warnings.length > 0 && !nodeGroupMismatches.length && (
        <div className="bg-yellow-50 border-l-2 border-yellow-500 p-2 rounded-sm">
          <div className="flex items-start">
            <FontAwesomeIcon icon={faExclamationTriangle} className="text-yellow-600 mt-0.5 mr-1.5 text-xs" />
            <div className="flex-1">
              <h3 className="text-xs font-semibold text-yellow-900 mb-0.5">Warnings</h3>
              <ul className="space-y-0.5">
                {upgradeAnalysis.warnings.map((warning: any, index: number) => (
                  <li key={index} className="text-xs text-yellow-800">
                    {warning.message}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* Vertical Tab Menu */}
      <div className="flex gap-2">
        {/* Sidebar */}
        <div className="w-32 flex-shrink-0">
          <div className="bg-white border border-gray-200 rounded p-1 space-y-1">
            <button
              onClick={() => handleTabChange('overview')}
              className={`w-full text-left px-2 py-1.5 rounded text-xs transition-colors ${
                activeTab === 'overview'
                  ? 'bg-blue-600 text-white'
                  : 'text-gray-700 hover:bg-gray-100'
              }`}
            >
              Overview
            </button>
            <button
              onClick={() => handleTabChange('nodes')}
              className={`w-full text-left px-2 py-1.5 rounded text-xs transition-colors ${
                activeTab === 'nodes'
                  ? 'bg-blue-600 text-white'
                  : 'text-gray-700 hover:bg-gray-100'
              }`}
            >
              Nodes ({nodes.length})
            </button>
            <button
              onClick={() => handleTabChange('pods')}
              className={`w-full text-left px-2 py-1.5 rounded text-xs transition-colors ${
                activeTab === 'pods'
                  ? 'bg-blue-600 text-white'
                  : 'text-gray-700 hover:bg-gray-100'
              }`}
            >
              Pods {loadingPods ? '...' : pods.length > 0 ? `(${pods.length})` : ''}
            </button>
          </div>
        </div>

        {/* Content Area */}
        <div className="flex-1">
          {activeTab === 'overview' && (
            <div className="space-y-2">
              {/* Stats Cards */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                <div className="bg-white border border-gray-200 rounded p-2">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-xs text-gray-500">Nodes</div>
                      <div className="text-sm font-bold">{nodes.length}</div>
                    </div>
                    <FontAwesomeIcon icon={faServer} className="text-blue-500 text-sm" />
                  </div>
                </div>

                <div className="bg-white border border-gray-200 rounded p-2">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-xs text-gray-500">Ready</div>
                      <div className="text-sm font-bold text-green-600">{nodes.filter(n => n.status === 'Ready').length}</div>
                    </div>
                    <FontAwesomeIcon icon={faCheckCircle} className="text-green-500 text-sm" />
                  </div>
                </div>

                <div className="bg-white border border-gray-200 rounded p-2">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-xs text-gray-500">Pods</div>
                      <div className="text-sm font-bold">{pods.length || '-'}</div>
                    </div>
                    <FontAwesomeIcon icon={faNetworkWired} className="text-gray-400 text-sm" />
                  </div>
                </div>

                <div className="bg-white border border-gray-200 rounded p-2">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-xs text-gray-500">Health</div>
                      <div className="text-sm font-bold text-green-600">OK</div>
                    </div>
                    <FontAwesomeIcon icon={faCheckCircle} className="text-green-500 text-sm" />
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'nodes' && (
            <div className="bg-white border border-gray-200 rounded p-2">
              <h2 className="text-sm font-bold text-gray-900 mb-2">Nodes</h2>
              {loading ? (
                <div className="text-center py-4 text-xs text-gray-500">
                  <FontAwesomeIcon icon={faSync} className="animate-spin mr-1" />
                  Loading...
                </div>
              ) : nodes.length === 0 ? (
                <div className="text-center py-4 text-xs text-gray-500">No nodes</div>
              ) : (
                <div className="overflow-x-auto -mx-2">
                  <table className="w-full text-xs">
                    <thead className="bg-gray-50 border-y border-gray-200">
                      <tr>
                        <th className="px-2 py-1.5 text-left text-xs font-semibold text-gray-600">Name</th>
                        <th className="px-2 py-1.5 text-left text-xs font-semibold text-gray-600">Status</th>
                        <th className="px-2 py-1.5 text-left text-xs font-semibold text-gray-600">Version</th>
                        <th className="px-2 py-1.5 text-left text-xs font-semibold text-gray-600">CPU</th>
                        <th className="px-2 py-1.5 text-left text-xs font-semibold text-gray-600">Memory</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {nodes.map((node, index) => {
                        const nodeVersion = node.version?.replace('v', '');
                        const isVersionMismatch = clusterData?.currentVersion && nodeVersion !== clusterData.currentVersion;
                        return (
                          <tr key={index} className="hover:bg-gray-50">
                            <td className="px-2 py-1.5 text-xs text-gray-900">{node.name}</td>
                            <td className="px-2 py-1.5">
                              <span className={`px-1.5 py-0.5 rounded text-xs ${
                                node.status === 'Ready' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                              }`}>
                                {node.status}
                              </span>
                            </td>
                            <td className="px-2 py-1.5">
                              <span className={`text-xs ${isVersionMismatch ? 'text-red-600 font-semibold' : 'text-gray-700'}`}>
                                {node.version}
                                {isVersionMismatch && <span className="ml-0.5">⚠️</span>}
                              </span>
                            </td>
                            <td className="px-2 py-1.5 text-xs text-gray-600">{node.capacity?.cpu || 'N/A'}</td>
                            <td className="px-2 py-1.5 text-xs text-gray-600">{node.capacity?.memory || 'N/A'}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {activeTab === 'pods' && (
            <div className="bg-white border border-gray-200 rounded p-2">
              <h2 className="text-sm font-bold text-gray-900 mb-2">Pods</h2>
              {loadingPods ? (
                <div className="text-center py-4 text-xs text-gray-500">
                  <FontAwesomeIcon icon={faSync} className="animate-spin mr-1" />
                  Loading pods...
                </div>
              ) : pods.length === 0 ? (
                <div className="text-center py-4 text-xs text-gray-500">
                  No pods found or not loaded yet
                  <button
                    onClick={fetchPods}
                    className="block mx-auto mt-2 px-3 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700"
                  >
                    Load Pods
                  </button>
                </div>
              ) : (
                <div className="overflow-x-auto -mx-2">
                  <table className="w-full text-xs">
                    <thead className="bg-gray-50 border-y border-gray-200">
                      <tr>
                        <th className="px-2 py-1.5 text-left text-xs font-semibold text-gray-600">Name</th>
                        <th className="px-2 py-1.5 text-left text-xs font-semibold text-gray-600">Namespace</th>
                        <th className="px-2 py-1.5 text-left text-xs font-semibold text-gray-600">Status</th>
                        <th className="px-2 py-1.5 text-left text-xs font-semibold text-gray-600">Node</th>
                        <th className="px-2 py-1.5 text-left text-xs font-semibold text-gray-600">Restarts</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {pods.map((pod, index) => (
                        <tr key={index} className="hover:bg-gray-50">
                          <td className="px-2 py-1.5 text-xs text-gray-900">{pod.name}</td>
                          <td className="px-2 py-1.5 text-xs text-gray-600">{pod.namespace}</td>
                          <td className="px-2 py-1.5">
                            <span className={`px-1.5 py-0.5 rounded text-xs ${
                              pod.status === 'Running' ? 'bg-green-100 text-green-700' :
                              pod.status === 'Pending' ? 'bg-yellow-100 text-yellow-700' :
                              pod.status === 'Failed' ? 'bg-red-100 text-red-700' :
                              'bg-gray-100 text-gray-700'
                            }`}>
                              {pod.status}
                            </span>
                          </td>
                          <td className="px-2 py-1.5 text-xs text-gray-600">{pod.nodeName || 'N/A'}</td>
                          <td className="px-2 py-1.5 text-xs text-gray-600">{pod.restartCount || 0}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
